import React, { useState } from 'react';
import { projectsAPI } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { Plus, X } from 'lucide-react';
import { cn } from '../lib/utils';

const formatCurrencyInput = (value) => {
  let digits = String(value).replace(/\D/g, '');
  if (!digits) return '';
  const num = parseInt(digits, 10);
  return (num / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};
const parseCurrencyInput = (value) => {
  const d = String(value).replace(/\D/g, '');
  return d ? parseInt(d, 10) / 100 : 0;
};
const fmtBRL = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

/**
 * Editor reutilizável de Parcelas do Serviço (Fase 4).
 * Funciona tanto em projetos ativos (ProjectTimeline) quanto em projetos
 * arquivados (ArchivedProjects), permitindo marcar pago/remover/adicionar.
 *
 * Props:
 *  - projectId: string
 *  - initialParcelas: array (opcional)
 *  - initialParcelado: bool (opcional)
 *  - readOnly: bool (opcional) - quando true, esconde formulário/remover
 *  - showModeToggle: bool (default true) - mostra toggle À Vista/Parcelado
 *  - compact: bool (default false) - layout enxuto para tabelas/expansões
 */
const ParcelasEditor = ({
  projectId,
  initialParcelas = [],
  initialParcelado = false,
  readOnly = false,
  showModeToggle = true,
  compact = false,
}) => {
  const [parcelado, setParcelado] = useState(initialParcelado === true);
  const [parcelas, setParcelas] = useState(initialParcelas || []);
  const [form, setForm] = useState({ valor: '', data_vencimento: '' });
  const [saving, setSaving] = useState(false);

  const handleToggleParcelado = async (next) => {
    try {
      await projectsAPI.setServicoModo(projectId, next);
      setParcelado(next);
      toast.success(next ? 'Modo Parcelado ativado' : 'Modo À Vista ativado');
    } catch {
      toast.error('Erro ao alterar modo do serviço');
    }
  };

  const handleAdd = async () => {
    const v = parseCurrencyInput(form.valor || '');
    if (!v || v <= 0) return toast.error('Valor inválido');
    if (!form.data_vencimento) return toast.error('Informe o vencimento');
    setSaving(true);
    try {
      const res = await projectsAPI.addParcela(projectId, {
        valor: v,
        data_vencimento: form.data_vencimento,
      });
      setParcelas((prev) => [...prev, res.data].sort((a, b) =>
        (a.data_vencimento || '').localeCompare(b.data_vencimento || '')));
      setForm({ valor: '', data_vencimento: '' });
      setParcelado(true);
      toast.success('Parcela adicionada');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao adicionar parcela');
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePago = async (id) => {
    try {
      const res = await projectsAPI.toggleParcelaPago(projectId, id);
      setParcelas((prev) => prev.map((p) => (p.id === id ? res.data : p)));
      toast.success(res.data.pago ? 'Parcela marcada como paga' : 'Parcela desmarcada');
    } catch {
      toast.error('Erro ao alterar status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remover esta parcela?')) return;
    try {
      await projectsAPI.deleteParcela(projectId, id);
      setParcelas((prev) => prev.filter((p) => p.id !== id));
      toast.success('Parcela removida');
    } catch {
      toast.error('Erro ao remover parcela');
    }
  };

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const sorted = [...parcelas].sort((a, b) =>
    (a.data_vencimento || '').localeCompare(b.data_vencimento || ''));
  const total = sorted.reduce((s, p) => s + (p.valor || 0), 0);
  const totalPago = sorted.filter((p) => p.pago).reduce((s, p) => s + (p.valor || 0), 0);
  const totalPendente = total - totalPago;

  return (
    <div className={cn('space-y-3', !compact && 'pt-3 border-t')} data-testid="parcelas-editor">
      {showModeToggle && !readOnly && (
        <div className="flex items-center gap-3 flex-wrap">
          <Label className="text-sm font-semibold">Forma de Pagamento:</Label>
          <div className="inline-flex border rounded-md overflow-hidden">
            <button
              type="button"
              onClick={() => handleToggleParcelado(false)}
              className={cn('px-3 py-1 text-xs font-medium transition-colors',
                !parcelado ? 'bg-primary text-primary-foreground' : 'bg-card hover:bg-muted')}
              data-testid="btn-modo-avista"
            >À Vista</button>
            <button
              type="button"
              onClick={() => handleToggleParcelado(true)}
              className={cn('px-3 py-1 text-xs font-medium transition-colors border-l',
                parcelado ? 'bg-primary text-primary-foreground' : 'bg-card hover:bg-muted')}
              data-testid="btn-modo-parcelado"
            >Parcelado</button>
          </div>
        </div>
      )}

      {(parcelado || sorted.length > 0) && (
        <>
          {sorted.length > 0 && (
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className="p-2 rounded border bg-muted/20">
                <p className="text-muted-foreground">Total</p>
                <p className="font-semibold">{fmtBRL(total)}</p>
              </div>
              <div className="p-2 rounded border bg-emerald-500/5 border-emerald-500/20">
                <p className="text-emerald-700 dark:text-emerald-400">Pago</p>
                <p className="font-semibold text-emerald-700 dark:text-emerald-400">{fmtBRL(totalPago)}</p>
              </div>
              <div className="p-2 rounded border bg-amber-500/5 border-amber-500/20">
                <p className="text-amber-700 dark:text-amber-400">Pendente</p>
                <p className="font-semibold text-amber-700 dark:text-amber-400">{fmtBRL(totalPendente)}</p>
              </div>
            </div>
          )}

          {sorted.length > 0 && (
            <div className="space-y-1.5">
              {sorted.map((p, idx) => {
                const pd = p.data_vencimento ? new Date(`${p.data_vencimento}T00:00:00`) : null;
                const isPast = pd && pd < today;
                const isToday = pd && pd.toDateString() === today.toDateString();
                return (
                  <div
                    key={p.id}
                    className={cn(
                      'flex items-center gap-2 p-2 rounded border text-sm',
                      p.pago ? 'bg-emerald-500/5 border-emerald-500/20' :
                      isToday ? 'bg-amber-500/10 border-amber-500/30' :
                      isPast ? 'bg-red-500/5 border-red-500/30' :
                      'bg-card border-border'
                    )}
                    data-testid={`parcela-item-${p.id}`}
                  >
                    <Checkbox
                      checked={!!p.pago}
                      onCheckedChange={() => handleTogglePago(p.id)}
                      data-testid={`parcela-pago-${p.id}`}
                    />
                    <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-[60px_1fr_auto] gap-2 items-center">
                      <span className="text-xs text-muted-foreground">#{idx + 1}</span>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={cn('font-mono text-xs', p.pago && 'line-through text-muted-foreground')}>
                          {pd ? pd.toLocaleDateString('pt-BR') : p.data_vencimento}
                        </span>
                        {isToday && !p.pago && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500 text-white font-medium">HOJE</span>}
                        {isPast && !p.pago && !isToday && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500 text-white font-medium">VENCIDA</span>}
                        {p.pago && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500 text-white font-medium">PAGA</span>}
                      </div>
                      <span className={cn('font-semibold text-sm whitespace-nowrap', p.pago && 'line-through text-muted-foreground')}>
                        {fmtBRL(p.valor)}
                      </span>
                    </div>
                    {!readOnly && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 flex-shrink-0"
                        onClick={() => handleDelete(p.id)}
                        data-testid={`parcela-delete-${p.id}`}
                        title="Remover parcela"
                      >
                        <X className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {!readOnly && (
            <>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  type="text"
                  placeholder="R$ 0,00"
                  value={form.valor}
                  onChange={(e) => setForm((f) => ({ ...f, valor: formatCurrencyInput(e.target.value) }))}
                  className="sm:w-32"
                  data-testid="parcela-valor-input"
                />
                <Input
                  type="date"
                  value={form.data_vencimento}
                  onChange={(e) => setForm((f) => ({ ...f, data_vencimento: e.target.value }))}
                  className="sm:w-40"
                  data-testid="parcela-data-input"
                />
                <Button onClick={handleAdd} disabled={saving} size="sm" data-testid="parcela-add-btn">
                  <Plus className="w-4 h-4 mr-1" /> Adicionar parcela
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Apenas parcelas marcadas como <strong>pagas</strong> são somadas no Dashboard.
              </p>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ParcelasEditor;
